import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Sparkles, RotateCw, Plus, Pencil, Trash2, Image as ImageIcon, Check, RefreshCw, X } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const CATEGORY_STYLES = {
  Feature: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  Improvement: 'bg-violet-100 text-violet-700 ring-violet-200',
  Fix: 'bg-amber-100 text-amber-700 ring-amber-200',
  Breaking: 'bg-rose-100 text-rose-700 ring-rose-200',
};

const EMPTY_FORM = { title: '', description: '', category: 'Feature', module: '', version: '', screenshot_url: '' };

export default function WhatsNewPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [entries, setEntries] = useState([]);
  const [modules, setModules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterModule, setFilterModule] = useState('all');
  const [filterRead, setFilterRead] = useState('all');

  // dialog state
  const [editing, setEditing] = useState(null); // { ...entry } | null
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterModule !== 'all') params.module = filterModule;
      const res = await api.get('/whats-new', { params });
      setEntries(res.data.entries);
      setModules(res.data.modules);
      setCategories(res.data.categories);
    } catch (e) { toast.error('Failed to load updates'); } finally { setLoading(false); }
  }, [filterCategory, filterModule]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // Mark all visible (not yet read) as read on view — fire-and-forget after a small delay
  useEffect(() => {
    const unread = entries.filter(e => !e.is_read).map(e => e.id);
    if (unread.length === 0) return;
    const t = setTimeout(async () => {
      try {
        await api.post('/whats-new/mark-read', { entry_ids: unread });
        // Update locally
        setEntries(prev => prev.map(e => unread.includes(e.id) ? { ...e, is_read: true } : e));
        // Notify any listeners (sidebar badge)
        window.dispatchEvent(new CustomEvent('whats-new-read'));
      } catch {}
    }, 1500);
    return () => clearTimeout(t);
  }, [entries]);

  const filtered = useMemo(() => {
    if (filterRead === 'unread') return entries.filter(e => !e.is_read);
    return entries;
  }, [entries, filterRead]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const e of filtered) {
      const key = `${e.version} • ${e.published_at}`;
      if (!map.has(key)) map.set(key, { version: e.version, date: e.published_at, items: [] });
      map.get(key).items.push(e);
    }
    return Array.from(map.values());
  }, [filtered]);

  const onSyncChangelog = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/whats-new/sync-changelog');
      toast.success(`Synced ${res.data.created} new, ${res.data.updated} updated`);
      await fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally { setSyncing(false); }
  };

  const onMarkAllRead = async () => {
    try {
      await api.post('/whats-new/mark-all-read');
      setEntries(prev => prev.map(e => ({ ...e, is_read: true })));
      window.dispatchEvent(new CustomEvent('whats-new-read'));
      toast.success('All caught up');
    } catch { toast.error('Failed to mark all read'); }
  };

  const openCreate = () => {
    setEditing({ id: null });
    setForm(EMPTY_FORM);
  };
  const openEdit = (entry) => {
    setEditing(entry);
    setForm({
      title: entry.title || '',
      description: entry.description || '',
      category: entry.category || 'Feature',
      module: entry.module || '',
      version: entry.version || '',
      screenshot_url: entry.screenshot_url || '',
    });
  };
  const closeDialog = () => { setEditing(null); setForm(EMPTY_FORM); };

  const onScreenshotUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Max 2 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(prev => ({ ...prev, screenshot_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const onSave = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing?.id) {
        await api.put(`/whats-new/${editing.id}`, payload);
        toast.success('Update saved');
      } else {
        await api.post('/whats-new', payload);
        toast.success('Update created');
      }
      closeDialog();
      await fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this update?')) return;
    try {
      await api.delete(`/whats-new/${id}`);
      toast.success('Deleted');
      await fetchEntries();
    } catch { toast.error('Delete failed'); }
  };

  const unreadCount = entries.filter(e => !e.is_read).length;

  return (
    <div data-testid="whats-new-page" className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
            <Sparkles className="w-7 h-7 text-violet-600" />
            What's New
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Recent features, improvements and fixes across the platform.
            {unreadCount > 0 && <span className="ml-2 text-violet-700 font-medium">{unreadCount} unread</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={onMarkAllRead} data-testid="mark-all-read" className="rounded-sm">
              <Check className="w-3.5 h-3.5 mr-2" /> Mark all read
            </Button>
          )}
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" onClick={onSyncChangelog} disabled={syncing} data-testid="sync-changelog" className="rounded-sm">
                <RotateCw className={`w-3.5 h-3.5 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                Sync from CHANGELOG.md
              </Button>
              <Button size="sm" onClick={openCreate} data-testid="new-entry" className="rounded-sm bg-violet-600 hover:bg-violet-700">
                <Plus className="w-3.5 h-3.5 mr-2" /> New update
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border border-border rounded-sm shadow-none">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[160px] h-9 rounded-sm" data-testid="filter-category">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterModule} onValueChange={setFilterModule}>
            <SelectTrigger className="w-[180px] h-9 rounded-sm" data-testid="filter-wn-module">
              <SelectValue placeholder="Module" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All modules</SelectItem>
              {modules.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterRead} onValueChange={setFilterRead}>
            <SelectTrigger className="w-[140px] h-9 rounded-sm" data-testid="filter-read">
              <SelectValue placeholder="Read state" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entries</SelectItem>
              <SelectItem value="unread">Unread only</SelectItem>
            </SelectContent>
          </Select>
          <span className="ml-auto text-xs text-muted-foreground font-data">{filtered.length} entries</span>
        </CardContent>
      </Card>

      {/* Timeline of updates */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500" />
        </div>
      ) : grouped.length === 0 ? (
        <Card className="border border-border rounded-sm">
          <CardContent className="p-12 text-center text-slate-400">
            <Sparkles className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No updates match the current filters.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {grouped.map(group => (
            <section key={group.version}>
              <div className="flex items-baseline gap-3 mb-3">
                <h2 className="font-heading text-lg font-bold text-slate-800">{group.version}</h2>
                <span className="text-xs text-slate-400 font-data">{group.date && format(new Date(group.date), 'dd MMM yyyy')}</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {group.items.map(entry => (
                  <Card
                    key={entry.id}
                    data-testid={`whats-new-card-${entry.id}`}
                    className={`border rounded-sm shadow-none transition-all ${entry.is_read ? 'border-border' : 'border-violet-300 ring-1 ring-violet-100'}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start gap-3">
                        <Badge className={`rounded-sm text-[10px] px-2 py-0.5 ring-1 hover:opacity-90 ${CATEGORY_STYLES[entry.category] || 'bg-slate-100 text-slate-700 ring-slate-200'}`}>
                          {entry.category}
                        </Badge>
                        {entry.module && <Badge variant="outline" className="rounded-sm text-[10px]">{entry.module}</Badge>}
                        {!entry.is_read && <span className="w-2 h-2 rounded-full bg-violet-500 animate-pulse mt-1.5" />}
                        {isAdmin && (
                          <div className="ml-auto flex items-center gap-1">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(entry)} data-testid={`edit-${entry.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700" onClick={() => onDelete(entry.id)} data-testid={`delete-${entry.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <h3 className="font-semibold text-slate-800 mt-3 leading-snug">{entry.title}</h3>
                      {entry.description && (
                        <p className="text-[13px] text-slate-600 leading-relaxed mt-1.5">{entry.description}</p>
                      )}
                      {entry.screenshot_url && (
                        <div className="mt-3 rounded-sm overflow-hidden border border-slate-200 bg-slate-50">
                          <img src={entry.screenshot_url} alt={entry.title} className="w-full h-auto object-cover" />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Edit / Create dialog */}
      <Dialog open={!!editing} onOpenChange={open => !open && closeDialog()}>
        <DialogContent data-testid="entry-dialog" className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit update' : 'New update'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-700">Title</label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-sm mt-1" data-testid="entry-title" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Description</label>
              <Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1" data-testid="entry-description" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700">Category</label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="entry-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Feature">Feature</SelectItem>
                    <SelectItem value="Improvement">Improvement</SelectItem>
                    <SelectItem value="Fix">Fix</SelectItem>
                    <SelectItem value="Breaking">Breaking</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Module</label>
                <Input value={form.module} onChange={e => setForm({ ...form, module: e.target.value })} className="rounded-sm mt-1" data-testid="entry-module" placeholder="e.g. Reports" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Version</label>
                <Input value={form.version} onChange={e => setForm({ ...form, version: e.target.value })} className="rounded-sm mt-1" data-testid="entry-version" placeholder="1.6.0" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Screenshot (optional, max 2 MB)</label>
              <div className="mt-1 flex items-center gap-3">
                <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-sm text-xs hover:bg-slate-50">
                  <ImageIcon className="w-3.5 h-3.5" /> Choose file
                  <input type="file" accept="image/*" className="hidden" onChange={onScreenshotUpload} data-testid="entry-screenshot" />
                </label>
                {form.screenshot_url && (
                  <div className="relative">
                    <img src={form.screenshot_url} alt="preview" className="h-12 rounded-sm border border-slate-200" />
                    <button onClick={() => setForm({ ...form, screenshot_url: '' })} className="absolute -top-2 -right-2 bg-white border border-slate-200 rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} className="rounded-sm">Cancel</Button>
            <Button onClick={onSave} disabled={saving} data-testid="entry-save" className="rounded-sm bg-violet-600 hover:bg-violet-700">
              {saving ? <RefreshCw className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-2" />}
              {editing?.id ? 'Save' : 'Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
